/**
 * Shopify Cart Quantity Limiter
 * Enforces a maximum of 1 unit per product variant in the cart.
 *
 * HOW TO INSTALL:
 * 1. In your Shopify admin, go to Online Store > Themes > Edit Code
 * 2. Open layout/theme.liquid
 * 3. Paste this entire script just before the closing </body> tag
 *    (wrap it in <script> tags if adding inline, or upload as an asset
 *     and reference it with a <script src="..."> tag)
 */

(function () {
  const MAX_QTY = 1;

  // ─── 1. INTERCEPT ADD-TO-CART FORM SUBMISSIONS ───────────────────────────
  //
  // Catches both standard form submits and fetch/XHR-based add-to-cart calls
  // used by most modern Shopify themes (Dawn, Debut, etc.).

  // Override the native fetch so we can intercept cart/add requests
  const _fetch = window.fetch;
  window.fetch = async function (input, init = {}) {
    const url = typeof input === "string" ? input : input.url;

    if (url && url.includes("/cart/add")) {
      // Parse body to find variant + requested quantity
      let body = init.body;
      let parsedBody = {};

      if (typeof body === "string") {
        try {
          parsedBody = JSON.parse(body);
        } catch {
          // FormData-encoded string
          const params = new URLSearchParams(body);
          parsedBody = Object.fromEntries(params.entries());
        }
      } else if (body instanceof FormData) {
        for (const [k, v] of body.entries()) parsedBody[k] = v;
      }

      // Force quantity to 1
      if (parsedBody.quantity !== undefined) {
        parsedBody.quantity = MAX_QTY;
        const contentType =
          (init.headers && (init.headers["Content-Type"] || init.headers["content-type"])) || "";

        if (contentType.includes("application/json")) {
          init.body = JSON.stringify(parsedBody);
        } else {
          const fd = new FormData();
          for (const [k, v] of Object.entries(parsedBody)) fd.append(k, v);
          init.body = fd;
        }
      }

      // After the item is added, immediately correct the cart if needed
      const response = await _fetch(input, init);
      await enforceCartLimits();
      return response;
    }

    return _fetch(input, init);
  };

  // Also intercept XMLHttpRequest (some older themes use jQuery.ajax / $.post)
  const _open = XMLHttpRequest.prototype.open;
  const _send = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this._url = url;
    return _open.apply(this, [method, url, ...rest]);
  };

  XMLHttpRequest.prototype.send = function (body) {
    if (this._url && this._url.includes("/cart/add")) {
      // After the XHR completes, enforce limits
      this.addEventListener("load", () => enforceCartLimits());
    }
    return _send.apply(this, [body]);
  };

  // ─── 2. ENFORCE LIMITS ON THE EXISTING CART ──────────────────────────────
  //
  // Calls /cart/change.js for any line item whose quantity > 1.

  async function enforceCartLimits() {
    try {
      const res = await _fetch("/cart.js");
      const cart = await res.json();

      for (const item of cart.items) {
        if (item.quantity > MAX_QTY) {
          await _fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: item.key, quantity: MAX_QTY }),
          });
        }
      }

      // Refresh the cart UI if the theme exposes a global updater
      if (typeof refreshCart === "function") refreshCart();
      if (typeof Shopify !== "undefined" && typeof Shopify.onError === "function") return;

      // Dawn / Hydrogen themes: dispatch an event the cart-drawer listens to
      document.dispatchEvent(new CustomEvent("cart:refresh"));

      // Also update any quantity input fields visible on the page
      syncQuantityInputs();
    } catch (err) {
      console.warn("[CartLimit] Could not enforce quantity limits:", err);
    }
  }

  // ─── 3. LOCK QUANTITY INPUTS IN THE CART PAGE / DRAWER ───────────────────
  //
  // Prevents users from typing or clicking +/- to raise qty above 1.

  function syncQuantityInputs() {
    // Set all quantity inputs to 1 and disable them
    document
      .querySelectorAll('input[name="updates[]"], input[name="quantity"], .quantity__input, input.qty')
      .forEach((input) => {
        input.value = MAX_QTY;
        input.setAttribute("max", MAX_QTY);
        input.setAttribute("min", MAX_QTY);
        input.setAttribute("readonly", true);
        input.style.pointerEvents = "none";
      });

    // Disable "+" / increment buttons
    document
      .querySelectorAll(
        '.quantity__button[name="+"], .qty-btn[data-func="increase"], button[aria-label*="Increase"], button[aria-label*="increase"], .increase, [data-action="increase-quantity"]'
      )
      .forEach((btn) => {
        btn.setAttribute("disabled", true);
        btn.style.opacity = "0.4";
        btn.style.cursor = "not-allowed";
        btn.style.pointerEvents = "none";
      });
  }

  // ─── 4. INTERCEPT STANDARD FORM SUBMITS (non-JS themes) ──────────────────

  document.addEventListener(
    "submit",
    function (e) {
      const form = e.target;
      if (!form || form.action === undefined) return;
      if (!form.action.includes("/cart/add")) return;

      const qtyInput = form.querySelector('[name="quantity"]');
      if (qtyInput) qtyInput.value = MAX_QTY;
    },
    true // capture phase — runs before the theme's own handlers
  );

  // ─── 5. OBSERVE DOM CHANGES (for cart drawers that render dynamically) ───

  const observer = new MutationObserver(() => syncQuantityInputs());
  observer.observe(document.body, { childList: true, subtree: true });

  // ─── 6. RUN ON PAGE LOAD ──────────────────────────────────────────────────

  document.addEventListener("DOMContentLoaded", () => {
    enforceCartLimits();
    syncQuantityInputs();
  });

  // Run immediately too (in case DOMContentLoaded already fired)
  if (document.readyState !== "loading") {
    enforceCartLimits();
    syncQuantityInputs();
  }
})();